import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  description?: string;
  trend?: {
    value: number;
    direction: "up" | "down" | "neutral";
    label?: string;
  };
  variant?: "default" | "success" | "warning" | "error";
  loading?: boolean;
  className?: string;
  onClick?: () => void;
}

const variantStyles = {
  default: {
    icon: "text-blue-500",
    trend: {
      up: "text-green-600",
      down: "text-red-600",
      neutral: "text-muted-foreground",
    },
  },
  success: {
    icon: "text-green-500",
    trend: {
      up: "text-green-600",
      down: "text-red-600",
      neutral: "text-muted-foreground",
    },
  },
  warning: {
    icon: "text-yellow-500",
    trend: {
      up: "text-yellow-600",
      down: "text-green-600",
      neutral: "text-muted-foreground",
    },
  },
  error: {
    icon: "text-red-500",
    trend: {
      up: "text-red-600",
      down: "text-green-600",
      neutral: "text-muted-foreground",
    },
  },
};

// Get appropriate text size class based on value length
function getValueSizeClass(value: string | number): string {
  const strValue = String(value);
  const len = strValue.length;

  if (len <= 4) return "text-2xl";
  if (len <= 8) return "text-xl";
  if (len <= 12) return "text-lg";
  if (len <= 16) return "text-base";
  return "text-sm";
}

export function MetricCard({
  title,
  value,
  icon: Icon,
  description,
  trend,
  variant = "default",
  loading = false,
  className,
  onClick,
}: MetricCardProps) {
  const styles = variantStyles[variant];

  const TrendIcon =
    trend?.direction === "up"
      ? TrendingUp
      : trend?.direction === "down"
      ? TrendingDown
      : Minus;

  const valueSizeClass = getValueSizeClass(value);

  return (
    <Card
      className={cn(
        "relative overflow-hidden",
        onClick && "cursor-pointer transition-colors hover:bg-muted/50",
        className
      )}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground truncate pr-2">
          {title}
        </CardTitle>
        <Icon className={cn("h-4 w-4 flex-shrink-0", styles.icon)} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse">
            <div className="h-8 w-24 bg-muted rounded mb-2" />
            {description && <div className="h-4 w-32 bg-muted rounded" />}
          </div>
        ) : (
          <>
            <div
              className={cn(
                "font-bold truncate",
                valueSizeClass
              )}
              title={String(value)}
            >
              {value}
            </div>
            {description && (
              <p className="text-xs text-muted-foreground mt-1 truncate" title={description}>
                {description}
              </p>
            )}
            {trend && (
              <div
                className={cn(
                  "flex items-center gap-1 text-xs mt-2",
                  styles.trend[trend.direction]
                )}
              >
                <TrendIcon className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">
                  {trend.direction === "up" ? "+" : ""}
                  {trend.value}%
                </span>
                {trend.label && (
                  <span className="text-muted-foreground ml-1 truncate">
                    {trend.label}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface MetricCardGridProps {
  children: React.ReactNode;
  columns?: 2 | 3 | 4;
}

export function MetricCardGrid({ children, columns = 3 }: MetricCardGridProps) {
  const gridClass = {
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <div className={cn("grid gap-4", gridClass[columns])}>
      {children}
    </div>
  );
}
